/**
 * The Meta ad composer's state, and the only two ways across the boundary
 * between it and the contract: form → request, and draft → form.
 *
 * ## WHY THE STATE IS STRINGS
 *
 * Every field a person types is held as the string they typed — the budget as
 * `"2000.50"`, ages as `"18"` — because that is what an input holds, and
 * converting on every keystroke loses what they meant half way through typing
 * it ("2000." is not a number yet). Conversion happens once, in
 * {@link requestFromForm}, through `metaAdDraftRequestSchema` — the same schema
 * the API parses with — and then through `draftFromRequest`, the same
 * conversion the API makes, in the currency the page showed beside the amount.
 * So the form refuses what the API would refuse, on the field that caused it,
 * with one exception it cannot see: an end date already past in the ad
 * account's zone, which the API decides on its own clock and reports on the
 * same field.
 *
 * ## THE SPECIAL CATEGORY STARTS UNCHOSEN, AND STAYS THAT WAY UNTIL SOMEBODY CHOOSES
 *
 * `specialAdCategory: null` is the initial state, and `null` does not convert:
 * the request refuses it. That is the contract's "no default" carried to the
 * screen — the category is a declaration a bank makes to Meta, and a form that
 * pre-selected "None" would be making it for them.
 *
 * When a restricted category is chosen, {@link withCategory} sets the ages to
 * the full range and gender to everyone, **visibly**, and the composer locks
 * those fields. It never quietly rewrites them at save time — a person who
 * narrowed age and then chose "Credit" sees the fields change in front of them.
 *
 * ## MONEY COMES BACK EXACTLY
 *
 * {@link formFromDraft} turns stored basic units back into the string that was
 * typed — 200050 → "2000.50" — with integer arithmetic, not division, so an
 * edit round-trips without a float ever touching the budget. The composer goes
 * through {@link formForAccount}, which carries the number across only when the
 * account still reports in the currency the draft was saved in.
 *
 * ## A WEBSITE OR A WHATSAPP CHAT (SLICE 47)
 *
 * The form holds where the draft's ads lead as one choice, and each ad's
 * format as the media a person picks — single image, carousel or video —
 * whatever the destination; {@link creativeFrom} makes a chat format of an
 * image or a video when the destination is a WhatsApp chat, and
 * {@link formFromDraft} makes it back. Wend builds a chat ad from an image or
 * a video — Meta allows a chat carousel too, and Wend has not built one — so
 * {@link requestFromForm} says so on a carousel's format rather than sending a
 * draft whose ads disagree. Choosing a chat sets the objective to Traffic, and
 * WhatsApp Status brings Instagram with it — in the form, where the person
 * sees it happen, as a restricted category does with ages.
 *
 * ## AND WHAT IS ALREADY AT META (SLICE 52)
 *
 * A publish Meta stopped part way leaves ids on the record. {@link frozenAtMeta}
 * reads them and says which parts of the form a correction may change — see
 * its own comment for the rule and for why it is per Meta object rather than
 * per form section.
 */
import {
  META_AGE_MAX,
  META_AGE_MIN,
  META_BUDGET_LABELS,
  META_RESTRICTED_TARGETING_CATEGORIES,
  draftFromRequest,
  metaAdDraftRequestSchema,
  metaCurrencyOffset,
  metaDraftCorrection,
  metaDraftDestination,
  type MetaAdCreative,
  type MetaAdDestination,
  type MetaAdDraft,
  type MetaAdDraftRequest,
  type MetaAdObjective,
  type MetaCta,
  type MetaPublishedIds,
  type MetaSpecialAdCategory,
} from '@wend/shared';

/**
 * What the Budget select offers, under the field labelled "Budget".
 *
 * Taken off `META_BUDGET_LABELS` rather than written here, because the same
 * amount is drawn on the draft's page, in the delivery figures, in the start
 * confirmation and in the budget form, and this select used to be the third
 * wording of it: "In total" beside "Budget in total" beside "Budget for the
 * whole run". The label above the select supplies the word "Budget", so the
 * option is the money screens' label with that word taken off the front —
 * which makes the two agree by construction rather than by anybody noticing.
 */
const withoutBudget = (label: string): string => {
  const rest = label.replace(/^Budget /, '');
  return rest.charAt(0).toUpperCase() + rest.slice(1);
};
export const BUDGET_KIND_LABELS: Readonly<Record<'daily' | 'lifetime', string>> = {
  daily: withoutBudget(META_BUDGET_LABELS.ad_set.daily),
  lifetime: withoutBudget(META_BUDGET_LABELS.ad_set.lifetime),
};

/**
 * The media a person picks for an ad. Whether it opens a website or a chat is
 * the draft's destination — see the header.
 */
export type CreativeFormat = 'single_image' | 'carousel' | 'single_video';

export interface CardForm {
  imageUrl: string;
  headline: string;
  description: string;
  link: string;
}

export interface AdForm {
  name: string;
  format: CreativeFormat;
  imageUrl: string;
  videoUrl: string;
  thumbnailUrl: string;
  primaryText: string;
  headline: string;
  description: string;
  cta: MetaCta;
  link: string;
  cards: CardForm[];
}

export interface DraftForm {
  campaignName: string;
  objective: MetaAdObjective;
  /** `null` until somebody chooses. See the header. */
  specialAdCategory: MetaSpecialAdCategory | null;
  pageId: string;
  instagramAccountId: string;
  adSetName: string;
  budgetKind: 'daily' | 'lifetime';
  budgetAmount: string;
  /** `YYYY-MM-DD`, or `''` for none. */
  endsOn: string;
  /** Comma-separated ISO codes, as typed. */
  countries: string;
  ageMin: string;
  ageMax: string;
  gender: 'all' | 'male' | 'female';
  /** `true` is Advantage+ placements — everywhere. */
  automaticPlacements: boolean;
  facebook: boolean;
  instagram: boolean;
  /** Slice 47. WhatsApp Status — a chat draft's, with Instagram beside it. */
  whatsappStatus: boolean;
  /** Slice 47. Where every ad in the draft leads. */
  destination: MetaAdDestination;
  ads: AdForm[];
}

export function emptyCard(): CardForm {
  return { imageUrl: '', headline: '', description: '', link: '' };
}

export function emptyAd(): AdForm {
  return {
    name: '',
    format: 'single_image',
    imageUrl: '',
    videoUrl: '',
    thumbnailUrl: '',
    primaryText: '',
    headline: '',
    description: '',
    cta: 'LEARN_MORE',
    link: '',
    cards: [emptyCard(), emptyCard()],
  };
}

/** A new draft's form. No special category — see the header. */
export function emptyDraftForm(): DraftForm {
  return {
    campaignName: '',
    objective: 'OUTCOME_TRAFFIC',
    specialAdCategory: null,
    pageId: '',
    instagramAccountId: '',
    adSetName: '',
    budgetKind: 'daily',
    budgetAmount: '',
    endsOn: '',
    countries: 'IN',
    ageMin: String(META_AGE_MIN),
    ageMax: String(META_AGE_MAX),
    gender: 'all',
    automaticPlacements: true,
    facebook: true,
    instagram: true,
    whatsappStatus: false,
    destination: 'website',
    ads: [emptyAd()],
  };
}

/** Whether the chosen category forbids age and gender targeting. */
export function isRestricted(form: DraftForm): boolean {
  return form.specialAdCategory !== null && META_RESTRICTED_TARGETING_CATEGORIES.includes(form.specialAdCategory);
}

/**
 * Chooses a category — and, for a restricted one, sets ages and gender to what
 * Meta requires, in the form, where the person sees it happen.
 */
export function withCategory(form: DraftForm, category: MetaSpecialAdCategory): DraftForm {
  const next = { ...form, specialAdCategory: category };
  return META_RESTRICTED_TARGETING_CATEGORIES.includes(category)
    ? { ...next, ageMin: String(META_AGE_MIN), ageMax: String(META_AGE_MAX), gender: 'all' }
    : next;
}

/**
 * Chooses where the ads lead. A chat can only be a traffic ad, so choosing one
 * sets the objective to Traffic, visibly; leaving the chat takes WhatsApp
 * Status with it, since Status takes only chat ads.
 */
export function withDestination(form: DraftForm, destination: MetaAdDestination): DraftForm {
  return destination === 'whatsapp'
    ? { ...form, destination, objective: 'OUTCOME_TRAFFIC' }
    : { ...form, destination, whatsappStatus: false };
}

/** WhatsApp Status on or off. On brings Instagram with it — Status runs beside Instagram Stories. */
export function withStatus(form: DraftForm, checked: boolean): DraftForm {
  return checked ? { ...form, whatsappStatus: true, instagram: true } : { ...form, whatsappStatus: false };
}

/** Instagram on or off. Off takes WhatsApp Status with it, for the same reason. */
export function withInstagram(form: DraftForm, checked: boolean): DraftForm {
  return checked ? { ...form, instagram: true } : { ...form, instagram: false, whatsappStatus: false };
}

/**
 * Basic units → the string a person would have typed, exactly.
 *
 * The remainder first, then a division that is exact because what it divides
 * is a whole multiple of the offset — never a fractional `minor / offset`
 * floored afterwards. 200050 → "2000.50" and 200000 → "2000.00" for a
 * two-digit currency, 2000 → "2000" for a zero-digit one. `null` for a
 * currency this product has no offset for.
 */
export function majorAmountString(minor: number, currency: string): string | null {
  const offset = metaCurrencyOffset(currency);
  if (offset === null) return null;
  if (offset === 1) return String(minor);
  const digits = Math.round(Math.log10(offset));
  const remainder = minor % offset;
  const whole = (minor - remainder) / offset;
  return `${String(whole)}.${String(remainder).padStart(digits, '0')}`;
}

/** A stored draft → the form that edits it. */
export function formFromDraft(draft: MetaAdDraft, currency: string): DraftForm {
  const { targeting } = draft.adSet;
  return {
    campaignName: draft.campaign.name,
    objective: draft.campaign.objective,
    specialAdCategory: draft.campaign.specialAdCategory,
    pageId: draft.pageId,
    instagramAccountId: draft.instagramAccountId ?? '',
    adSetName: draft.adSet.name,
    budgetKind: draft.adSet.budget.kind,
    budgetAmount: majorAmountString(draft.adSet.budget.amount, currency) ?? '',
    endsOn: draft.adSet.endsOn ?? '',
    countries: targeting.countries.join(', '),
    ageMin: String(targeting.ageMin),
    ageMax: String(targeting.ageMax),
    gender: targeting.gender,
    automaticPlacements: targeting.placements === null,
    facebook: targeting.placements === null || targeting.placements.includes('facebook'),
    instagram: targeting.placements === null || targeting.placements.includes('instagram'),
    whatsappStatus: targeting.placements !== null && targeting.placements.includes('whatsapp'),
    destination: metaDraftDestination(draft),
    ads: draft.ads.map((ad) => adFormFrom(ad.name, ad.creative)),
  };
}

/**
 * The form that edits a stored draft **in the account's current currency**.
 *
 * The API reads whatever amount is saved in the currency the connected account
 * reports now. A draft saved as ₹2,000.50 and opened after an account that
 * reports in USD was connected must therefore not arrive with "2000.50"
 * already typed beside "Amount, USD": one press of Save would make it
 * $2,000.50, some eighty times the budget, with only the label to say so. The
 * amount is left empty instead — the form will not save until somebody types
 * one in the new currency — and the composer shows the old budget, as money in
 * its own currency, under the field.
 */
export function formForAccount(draft: MetaAdDraft, draftCurrency: string, accountCurrency: string): DraftForm {
  const form = formFromDraft(draft, draftCurrency);
  return draftCurrency === accountCurrency ? form : { ...form, budgetAmount: '' };
}

/* -------------------------------------------------------------------------- */
/* What already exists at Meta — slice 52, gap 1                               */
/* -------------------------------------------------------------------------- */

/**
 * Which parts of the form a correction may not touch, read off the ids a
 * stopped publish recorded.
 *
 * A publish creates the campaign, then the ad set, then a creative and an ad
 * per ad in the draft, recording each id the moment it exists. A refusal part
 * way therefore leaves a draft that is half at Meta: slice 42 refused to edit
 * any of it, so the only way past a budget under Meta's minimum — the
 * commonest refusal there is — was a new draft. Slice 52 opens the parts that
 * do not exist yet and freezes the parts that do, so the campaign already
 * created is re-used rather than orphaned beside a second one.
 *
 * The rule is per Meta object, and it is deliberately the shape of the publish
 * rather than the shape of the form:
 *
 *   - a **campaign** id freezes the campaign's name, objective and special ad
 *     category — the three fields that campaign was created from;
 *   - an **ad set** id freezes the ad set entire (name, budget, end,
 *     targeting, placements) and the Page, because an ad set carries its
 *     promoted page;
 *   - any **creative** freezes the Page and the Instagram account, because one
 *     Page across created and uncreated creatives would publish two different
 *     ads under one draft;
 *   - an ad with any of a video, a creative or an ad id is frozen whole.
 *
 * `metaDraftCorrection` in `@wend/shared` IS that rule, and {@link frozenAtMeta}
 * asks it rather than re-deriving it. This type exists so the refusal is
 * prevented rather than reported — a frozen field is drawn read-only, so
 * nobody types into it and then reads a sentence saying they may not — and its
 * only job is to group the paths the verdict freezes into the groups the form
 * has controls for. Two readings of one `published` were kept honest by a
 * cross-check at group level, which is exactly the altitude a control that
 * slipped out of a frozen group hid at; one reading cannot disagree with
 * itself, and what the suite checks now is that no path the verdict freezes
 * falls outside a group, and that every control in a frozen group is shut.
 *
 * Rejected: freezing by form section, which is how the design first read. The
 * Page and the Instagram account sit in one section and are frozen by
 * different objects — an ad set with no creatives yet freezes the Page alone —
 * so a section-shaped rule would leave the composer offering an edit the API
 * refuses, which is the one thing this design exists to avoid.
 */
export interface FrozenAtMeta {
  /** The campaign's name, objective and special ad category. */
  readonly campaign: boolean;
  /** The Facebook Page the ads run as. */
  readonly pageId: boolean;
  /** The Instagram account they run as. */
  readonly instagramAccountId: boolean;
  /** The ad set whole: name, budget, end, targeting, placements. */
  readonly adSet: boolean;
  /** Per ad in the draft, in the draft's own order. */
  readonly ads: readonly boolean[];
  /**
   * Whether anything at all exists at Meta. While it does the ads list may be
   * appended to and nothing else — no removal, no reorder — because
   * `published`'s three arrays are positional and a removal would leave
   * `creativeIds[i]` pointing at a creative belonging to a different ad.
   */
  readonly any: boolean;
}

/** Nothing at Meta: every field open, for a draft being composed or edited. */
export function frozenAtMetaNothing(adCount: number): FrozenAtMeta {
  return {
    campaign: false,
    pageId: false,
    instagramAccountId: false,
    adSet: false,
    ads: Array.from({ length: adCount }, () => false),
    any: false,
  };
}

/**
 * See {@link FrozenAtMeta}. `draft` is the record's as stored and `published`
 * the ids beside it.
 *
 * The rule itself is `metaDraftCorrection`'s, asked here for the draft against
 * itself: a correction that changes nothing raises no issue, and its `frozen`
 * paths are then exactly what is on file at Meta. Grouping those paths is all
 * this does — which is why an ad set that grows a field, or a new object Meta
 * starts holding, arrives here without anybody remembering to copy the rule
 * across.
 */
export function frozenAtMeta(draft: MetaAdDraft, published: MetaPublishedIds): FrozenAtMeta {
  const verdict = metaDraftCorrection(draft, draft, published);
  if (!verdict.atMeta) return frozenAtMetaNothing(draft.ads.length);
  const { frozen } = verdict;
  return {
    campaign: frozen.some((path) => path.startsWith('campaign.')),
    /* An ad set carries the promoted page; so does every creative. Both are
       the verdict's own reading — see `metaDraftCorrection`. */
    pageId: frozen.includes('pageId'),
    instagramAccountId: frozen.includes('instagramAccountId'),
    adSet: frozen.some((path) => path.startsWith('adSet.')),
    /* Appended ads sit past the end of the recorded arrays and are open. */
    ads: draft.ads.map((_, index) => frozen.includes(`ads.${String(index)}`)),
    any: verdict.atMeta,
  };
}

function adFormFrom(name: string, creative: MetaAdCreative): AdForm {
  const base = { ...emptyAd(), name, primaryText: creative.primaryText };
  switch (creative.format) {
    case 'single_image':
      return {
        ...base,
        format: 'single_image',
        cta: creative.cta,
        imageUrl: creative.imageUrl,
        headline: creative.headline,
        description: creative.description ?? '',
        link: creative.link,
      };
    case 'carousel':
      return {
        ...base,
        format: 'carousel',
        cta: creative.cta,
        cards: creative.cards.map((card) => ({
          imageUrl: card.imageUrl,
          headline: card.headline,
          description: card.description ?? '',
          link: card.link,
        })),
      };
    case 'single_video':
      return {
        ...base,
        format: 'single_video',
        cta: creative.cta,
        videoUrl: creative.videoUrl,
        thumbnailUrl: creative.thumbnailUrl,
        headline: creative.headline,
        description: creative.description ?? '',
        link: creative.link,
      };
    /* Slice 47: a chat ad is the same media with no link or button. */
    case 'chat_image':
      return {
        ...base,
        format: 'single_image',
        imageUrl: creative.imageUrl,
        headline: creative.headline,
        description: creative.description ?? '',
      };
    case 'chat_video':
      return {
        ...base,
        format: 'single_video',
        videoUrl: creative.videoUrl,
        thumbnailUrl: creative.thumbnailUrl,
        headline: creative.headline,
        description: creative.description ?? '',
      };
    default: {
      const exhaustive: never = creative;
      throw new Error(`Unhandled creative format: ${String(exhaustive)}`);
    }
  }
}

/** Blank optional text → `null`, which is what the contract means by "none". */
const optional = (value: string): string | null => (value.trim() === '' ? null : value.trim());

function creativeFrom(ad: AdForm, destination: MetaAdDestination): unknown {
  if (destination === 'whatsapp' && ad.format === 'single_image') {
    return {
      format: 'chat_image',
      imageUrl: ad.imageUrl.trim(),
      primaryText: ad.primaryText,
      headline: ad.headline,
      description: optional(ad.description),
    };
  }
  if (destination === 'whatsapp' && ad.format === 'single_video') {
    return {
      format: 'chat_video',
      videoUrl: ad.videoUrl.trim(),
      thumbnailUrl: ad.thumbnailUrl.trim(),
      primaryText: ad.primaryText,
      headline: ad.headline,
      description: optional(ad.description),
    };
  }
  switch (ad.format) {
    case 'single_image':
      return {
        format: 'single_image',
        imageUrl: ad.imageUrl.trim(),
        primaryText: ad.primaryText,
        headline: ad.headline,
        description: optional(ad.description),
        cta: ad.cta,
        link: ad.link.trim(),
      };
    case 'carousel':
      return {
        format: 'carousel',
        primaryText: ad.primaryText,
        cta: ad.cta,
        cards: ad.cards.map((card) => ({
          imageUrl: card.imageUrl.trim(),
          headline: card.headline,
          description: optional(card.description),
          link: card.link.trim(),
        })),
      };
    case 'single_video':
      return {
        format: 'single_video',
        videoUrl: ad.videoUrl.trim(),
        thumbnailUrl: ad.thumbnailUrl.trim(),
        primaryText: ad.primaryText,
        headline: ad.headline,
        description: optional(ad.description),
        cta: ad.cta,
        link: ad.link.trim(),
      };
    default: {
      const exhaustive: never = ad.format;
      throw new Error(`Unhandled creative format: ${String(exhaustive)}`);
    }
  }
}

/** The key the special-category refusal is reported under. */
export const CATEGORY_FIELD = 'campaign.specialAdCategory';

/**
 * What a carousel in a chat draft stands in as while the rest of the form is
 * checked — valid in every field, so it adds no refusal of its own. It is
 * never sent: the carousel's own refusal stops the save.
 */
const CAROUSEL_STAND_IN = {
  format: 'chat_image',
  imageUrl: 'https://stand-in.invalid/carousel.png',
  primaryText: 'stand-in',
  headline: 'stand-in',
  description: null,
} as const;

/** The key every budget refusal is reported under — the API's too. */
export const AMOUNT_FIELD = 'adSet.budget.amount';

/**
 * An issue's path → the field the composer shows it on.
 *
 * Almost always the path itself. The exception is a list typed into one input:
 * a bad country is reported by the schema at `adSet.targeting.countries.1`,
 * and there is no input at that path — the refusal would count towards "Some
 * fields need attention" while marking nothing. Every index under countries
 * lands on the one field they were typed into.
 */
export function fieldFor(path: string): string {
  return path.startsWith('adSet.targeting.countries.') ? 'adSet.targeting.countries' : path;
}

/**
 * The form → a request the API will accept, or the errors keyed by the field
 * that caused each — the same dotted paths the API reports, so a refusal from
 * either side lands on the same input.
 *
 * `currency` is the one the page showed beside the amount, and travels with
 * it: the API refuses an amount typed against a currency the account no longer
 * reports in, rather than reading "2000.50" as dollars.
 */
export function requestFromForm(
  form: DraftForm,
  currency: string,
): { readonly ok: true; readonly request: MetaAdDraftRequest } | { readonly ok: false; readonly errors: Record<string, string> } {
  if (form.specialAdCategory === null) {
    return {
      ok: false,
      errors: { [CATEGORY_FIELD]: 'Choose one — "No special category" included.' },
    };
  }

  /*
   * A chat ad is an image or a video here (see the header), so a carousel in a
   * chat draft is refused on its own format. Every other refusal is found in
   * the same pass: the carousel stands in the candidate as a chat image the
   * schema cannot fault — never sent — so the draft's own fields are still
   * checked, and the schema's "every ad opens the same place" never lands on
   * the wrong ad.
   */
  const carousels =
    form.destination === 'whatsapp'
      ? form.ads.flatMap((ad, index) => (ad.format === 'carousel' ? [index] : []))
      : [];

  const candidate = {
    campaign: { name: form.campaignName, objective: form.objective, specialAdCategory: form.specialAdCategory },
    pageId: form.pageId.trim(),
    instagramAccountId: optional(form.instagramAccountId),
    adSet: {
      name: form.adSetName,
      budget: { kind: form.budgetKind, amount: form.budgetAmount.trim(), currency },
      endsOn: optional(form.endsOn),
      targeting: {
        countries: countryCodes(form.countries),
        ageMin: Number(form.ageMin),
        ageMax: Number(form.ageMax),
        gender: form.gender,
        placements: form.automaticPlacements
          ? null
          : [
              ...(form.facebook ? ['facebook' as const] : []),
              ...(form.instagram ? ['instagram' as const] : []),
              ...(form.whatsappStatus ? ['whatsapp' as const] : []),
            ],
      },
    },
    ads: form.ads.map((ad, index) => ({
      name: ad.name,
      creative: carousels.includes(index) ? CAROUSEL_STAND_IN : creativeFrom(ad, form.destination),
    })),
  };

  const parsed = metaAdDraftRequestSchema.safeParse(candidate);
  const errors: Record<string, string> = {};
  for (const index of carousels) {
    errors[`ads.${String(index)}.creative.format`] = 'Choose a single image or a video.';
  }
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      errors[fieldFor(issue.path.join('.'))] ??= issue.message;
    }
  }
  if (Object.keys(errors).length > 0 || !parsed.success) return { ok: false, errors };

  /* The API's own conversion, in the currency shown: too many decimals for
     the currency, or nothing to spend, is said here, on the amount. */
  const converted = draftFromRequest(parsed.data, currency);
  if (!converted.ok) return { ok: false, errors: { [AMOUNT_FIELD]: converted.message } };

  return { ok: true, request: parsed.data };
}

/**
 * The country codes a comma-separated field holds: trimmed, upper-cased, blanks
 * dropped. One function, used by the request builder above and by the chips the
 * composer draws under the field (slice 66), so the chips are exactly what
 * would be sent — a second parser for the preview is how a chip would come to
 * show a country the request then refuses or never sends. It does not judge a
 * code: "INDIA" comes back as a chip and the schema refuses it on submit, on the
 * Countries field, as before.
 */
export function countryCodes(text: string): string[] {
  return text
    .split(',')
    .map((code) => code.trim().toUpperCase())
    .filter((code) => code !== '');
}
